import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import { ClipboardCheck, BarChart3, BookOpen, ShieldCheck, FileSignature } from "lucide-react";

import { ClosureChecklistTab } from "./closure/ClosureChecklistTab";
import { ResultsValidationTab } from "./closure/ResultsValidationTab";
import { LessonsLearnedTab } from "./closure/LessonsLearnedTab";
import { SustainabilityTab } from "./closure/SustainabilityTab";
import { SignOffTab } from "./closure/SignOffTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClosurePhase({ projectId, token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState("");

  // Closure data from backend
  const [closureData, setClosureData] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [resultsSummary, setResultsSummary] = useState(null);

  // Editable fields
  const [lessonsLearned, setLessonsLearned] = useState({ what_went_well: "", what_didnt: "", recommendations: "" });
  const [sustainability, setSustainability] = useState({
    control_plan_verified: false, monitoring_active: false,
    response_plan_verified: false, ownership_handoff: false, handoff_notes: "",
  });
  const [executiveSummary, setExecutiveSummary] = useState("");
  const [closureReason, setClosureReason] = useState("");
  const [closureStatus, setClosureStatus] = useState("completed");
  const [closureNotes, setClosureNotes] = useState("");
  const [aiDrafts, setAiDrafts] = useState({});

  // ---- Load ----
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/projects/${projectId}/closure`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to load closure data");
        const data = await res.json();
        setClosureData(data);
        setChecklist(data.checklist);
        setResultsSummary(data.results_summary);
        if (data.lessons_learned?.what_went_well) setLessonsLearned(data.lessons_learned);
        if (data.sustainability_confirmation?.control_plan_verified !== undefined) setSustainability(data.sustainability_confirmation);
        if (data.executive_summary) setExecutiveSummary(data.executive_summary);
        if (data.closure_reason) setClosureReason(data.closure_reason);
        if (data.closure_status) setClosureStatus(data.closure_status);
        if (data.closure_notes) setClosureNotes(data.closure_notes);
        if (data.ai_drafts) setAiDrafts(data.ai_drafts);
      } catch (error) {
        console.error("Failed to load closure:", error);
        toast.error("Failed to load closure data");
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId, token]);

  const isClosed = closureData?.is_closed || false;

  // ---- Save helpers ----
  const saveLessonsLearned = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/closure/lessons-learned`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(lessonsLearned),
      });
      if (!res.ok) throw new Error("Failed to save");
      toast.success("Lessons learned saved");
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const saveSustainability = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/closure/sustainability`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(sustainability),
      });
      if (!res.ok) throw new Error("Failed to save");
      toast.success("Sustainability confirmation saved");
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const saveDetails = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/closure/details`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          closure_reason: closureReason, closure_status: closureStatus,
          closure_notes: closureNotes, executive_summary: executiveSummary,
        }),
      });
      if (!res.ok) throw new Error("Failed to save");
      toast.success("Closure details saved");
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const performSignOff = async (status) => {
    if (!status) { toast.error("Please select a closure status"); return; }
    setSaving(true);
    try {
      // Save details first
      await fetch(`${API}/projects/${projectId}/closure/details`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          closure_reason: closureReason, closure_status: status,
          closure_notes: closureNotes, executive_summary: executiveSummary,
        }),
      });
      // Perform sign-off
      const res = await fetch(`${API}/projects/${projectId}/closure/sign-off`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ closure_status: status, comments: closureNotes }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Sign-off failed"); }
      toast.success("Project formally closed!");
      window.location.reload();
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  // ---- AI draft ----
  const generateAiDraft = async (draftType) => {
    setAiGenerating(draftType);
    try {
      const res = await fetch(`${API}/ai/closure-drafts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId, draft_type: draftType }),
      });
      if (!res.ok) throw new Error("AI generation failed");
      const data = await res.json();
      if (data.ai_unavailable) { toast.error("AI service unavailable"); return; }
      setAiDrafts((prev) => ({ ...prev, ...data }));
      toast.success("AI draft generated! Review before applying.");
    } catch (error) { toast.error(error.message); } finally { setAiGenerating(""); }
  };

  // ---- PDF export ----
  const exportPdf = async (reportType) => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/closure/export-pdf?report_type=${reportType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = reportType === "executive" ? "executive_summary.pdf" : "closure_report.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF exported");
    } catch (error) { toast.error(error.message); }
  };

  if (loading) return <div className="text-muted-foreground">Loading Closure phase...</div>;

  return (
    <div className="space-y-6" data-testid="closure-phase">
      <div>
        <h1 className="font-heading text-2xl font-bold">Project Closure</h1>
        <p className="text-muted-foreground">
          {isClosed
            ? "This project has been formally closed. Read-only mode."
            : "Complete the closure checklist, document lessons learned, and formally close the project."}
        </p>
      </div>

      <Tabs defaultValue="checklist" className="space-y-6">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="checklist" className="gap-2" data-testid="tab-closure-checklist">
            <ClipboardCheck className="w-4 h-4" /> Checklist
          </TabsTrigger>
          <TabsTrigger value="results" className="gap-2" data-testid="tab-closure-results">
            <BarChart3 className="w-4 h-4" /> Results
          </TabsTrigger>
          <TabsTrigger value="lessons" className="gap-2" data-testid="tab-closure-lessons">
            <BookOpen className="w-4 h-4" /> Lessons Learned
          </TabsTrigger>
          <TabsTrigger value="sustainability" className="gap-2" data-testid="tab-closure-sustainability">
            <ShieldCheck className="w-4 h-4" /> Sustainability
          </TabsTrigger>
          <TabsTrigger value="signoff" className="gap-2" data-testid="tab-closure-signoff">
            <FileSignature className="w-4 h-4" /> Sign-Off
          </TabsTrigger>
        </TabsList>

        <TabsContent value="checklist" className="space-y-6">
          <ClosureChecklistTab checklist={checklist} />
        </TabsContent>

        <TabsContent value="results" className="space-y-6">
          <ResultsValidationTab resultsSummary={resultsSummary} />
        </TabsContent>

        <TabsContent value="lessons" className="space-y-6">
          <LessonsLearnedTab
            lessonsLearned={lessonsLearned} setLessonsLearned={setLessonsLearned}
            aiDrafts={aiDrafts} saving={saving} aiGenerating={aiGenerating}
            saveLessonsLearned={saveLessonsLearned} generateAiDraft={generateAiDraft}
          />
        </TabsContent>

        <TabsContent value="sustainability" className="space-y-6">
          <SustainabilityTab
            sustainability={sustainability} setSustainability={setSustainability}
            aiDrafts={aiDrafts} saving={saving} aiGenerating={aiGenerating}
            saveSustainability={saveSustainability} generateAiDraft={generateAiDraft}
          />
        </TabsContent>

        <TabsContent value="signoff" className="space-y-6">
          <SignOffTab
            projectId={projectId} token={token}
            closureData={closureData} saving={saving} aiGenerating={aiGenerating}
            executiveSummary={executiveSummary} setExecutiveSummary={setExecutiveSummary}
            closureReason={closureReason} setClosureReason={setClosureReason}
            closureStatus={closureStatus} setClosureStatus={setClosureStatus}
            closureNotes={closureNotes} setClosureNotes={setClosureNotes}
            aiDrafts={aiDrafts} isClosed={isClosed}
            saveDetails={saveDetails} performSignOff={performSignOff}
            generateAiDraft={generateAiDraft} exportPdf={exportPdf}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
